import { firstName } from '@hudhuria/shared';
import { createFileRoute, useNavigate } from '@tanstack/react-router';
import { useTodayView } from '~/api/derived';
import { useCan, useCenter, useGroups, useMe } from '~/api/queries';
import { CheckIcon, ChevronDown, ChevronRight, GroupsIcon, UserPlusIcon } from '~/components/icons';
import { SyncPill } from '~/components/sync-pill';
import { Avatar } from '~/components/ui';
import { useSheet } from '~/lib/sheet';

export const Route = createFileRoute('/_app/_tabs/hub')({ component: Hub });

function Hub() {
  const navigate = useNavigate();
  const me = useMe().data;
  const center = useCenter();
  const view = useTodayView();
  const groups = useGroups().data ?? [];
  const canRegister = useCan('register');
  const isTeacher = me?.staff.role === 'teacher';

  const { open: openSheet } = useSheet();

  const stats = isTeacher
    ? [
        {
          value: view.checkinCount,
          label: 'Check-ins',
          color: '#0f172a',
          go: () => void navigate({ to: '/timeline', search: { f: 'in' } }),
        },
        {
          value: groups.length,
          label: 'My groups',
          color: '#047857',
          go: () => openSheet('checkin', 'groups'),
        },
        {
          value: view.rollCallCount,
          label: 'Roll-calls',
          color: '#0f172a',
          go: () => void navigate({ to: '/timeline', search: { f: 'group' } }),
        },
      ]
    : [
        {
          value: view.checkinCount,
          label: 'Check-ins',
          color: '#0f172a',
          go: () => void navigate({ to: '/timeline', search: { f: 'in' } }),
        },
        {
          value: view.rollCallCount,
          label: 'Groups',
          color: '#0f172a',
          go: () => void navigate({ to: '/timeline', search: { f: 'group' } }),
        },
        {
          value: view.unpaidCount,
          label: 'Unpaid',
          color: '#b45309',
          go: () => void navigate({ to: '/students', search: { f: 'owes' } }),
        },
      ];

  const arrivals = view.presentStudents;
  const arrivalsText = arrivals.length
    ? `${arrivals
        .slice(0, 2)
        .map((s) => firstName(s.name))
        .join(', ')}${view.present.size > 2 ? ` +${view.present.size - 2}` : ''} arrived`
    : 'No arrivals yet today';

  return (
    <div className="flex-1 animate-fade-in-fast px-[22px] pt-safe-8 pb-nav scroll-y">
      <div className="flex items-center justify-between">
        <button
          type="button"
          onClick={() => void navigate({ to: '/me' })}
          className="cursor-pointer border-none bg-transparent p-0 text-left"
        >
          <div className="flex items-center gap-1.5 text-[13.5px] text-slate-500">
            {center?.name}
            <ChevronDown />
          </div>
          <div className="mt-px font-display text-[23px] font-bold">Today</div>
        </button>
        <div className="flex items-center gap-2">
          <SyncPill />
          <button
            type="button"
            onClick={() => void navigate({ to: '/me' })}
            className="cursor-pointer border-none bg-transparent p-0"
          >
            <Avatar name={me?.staff.name ?? ''} size={40} radius="full" color="#0369a1" fontSize={13.5} />
          </button>
        </div>
      </div>

      <div className="mt-7 flex justify-center">
        <button
          type="button"
          onClick={() => openSheet('checkin')}
          aria-label="Check in"
          className="flex h-[244px] w-[244px] cursor-pointer items-center justify-center rounded-full border-none p-0 shadow-[0_18px_44px_rgba(3,105,161,0.22)]"
          style={{
            background: `conic-gradient(#38bdf8 ${view.pct}%, #e6f4fc ${view.pct}%)`,
            transition: 'background .4s',
          }}
        >
          <div className="flex h-[188px] w-[188px] flex-col items-center justify-center rounded-full bg-brand text-white">
            <div className="font-display text-[70px] leading-[0.9] font-bold tracking-[-0.03em]">
              {view.present.size}
            </div>
            <div className="mt-1.5 text-[14px] opacity-80">of {view.enrolled} present</div>
            <div className="mt-[10px] rounded-[20px] bg-white/20 px-[11px] py-[5px] text-[11.5px] font-bold tracking-[0.08em]">
              {view.pct}%
            </div>
          </div>
        </button>
      </div>

      <div className="mt-7 grid grid-cols-3 gap-[10px]">
        {stats.map((s) => (
          <button
            type="button"
            key={s.label}
            onClick={s.go}
            className="cursor-pointer rounded-2xl border-none bg-fill-soft px-[10px] py-[14px] text-center"
          >
            <div className="font-display text-[23px] font-bold" style={{ color: s.color }}>
              {s.value}
            </div>
            <div className="mt-[3px] text-[11.5px] text-slate-500">{s.label}</div>
          </button>
        ))}
      </div>

      <div className="mt-3 grid grid-cols-2 gap-[10px]">
        <Tile
          onClick={() => openSheet('checkin')}
          // teachers have three tiles: Check in takes the top row, Register and My groups share the next
          className={`bg-brand text-white ${canRegister && isTeacher ? 'col-span-2' : ''}`}
          icon={<CheckIcon />}
          title="Check in"
          sub="Groups or walk-ins"
          subClass="opacity-80"
        />
        {canRegister && (
          <Tile
            onClick={() => openSheet('register')}
            className="bg-fill-soft"
            icon={<UserPlusIcon className="text-brand" />}
            title="Register"
            sub="New student"
            subClass="text-slate-500"
          />
        )}
        {isTeacher && (
          <Tile
            onClick={() => openSheet('checkin', 'groups')}
            className="bg-emerald-50"
            icon={<GroupsIcon className="text-emerald-700" />}
            title={<span className="text-emerald-800">My groups</span>}
            sub="Roll-call your classes"
            subClass="text-emerald-700"
          />
        )}
      </div>

      <button
        type="button"
        onClick={() => void navigate({ to: '/timeline' })}
        className="mt-3 flex w-full cursor-pointer items-center gap-[10px] rounded-[18px] border border-line bg-white px-[15px] py-[13px] text-left"
      >
        {arrivals.length > 0 && (
          <div className="flex">
            {arrivals.slice(0, 3).map((s, i) => (
              <Avatar
                key={s.id}
                name={s.name}
                photoUrl={s.photoUrl}
                size={30}
                radius="full"
                fontSize={11}
                className="border-2 border-white"
                style={{ marginLeft: i ? -9 : 0 }}
              />
            ))}
          </div>
        )}
        <div className="flex-1 text-[13px] text-slate-700">{arrivalsText}</div>
        <ChevronRight color="#94a3b8" />
      </button>
    </div>
  );
}

function Tile({
  onClick,
  className,
  icon,
  title,
  sub,
  subClass,
}: {
  onClick: () => void;
  className: string;
  icon: React.ReactNode;
  title: React.ReactNode;
  sub: string;
  subClass: string;
}) {
  return (
    <button
      type="button"
      onClick={onClick}
      className={`flex cursor-pointer flex-col gap-[22px] rounded-[18px] border-none p-4 text-left ${className}`}
    >
      {icon}
      <div>
        <div className="font-display text-[16px] font-bold">{title}</div>
        <div className={`mt-0.5 text-[12px] ${subClass}`}>{sub}</div>
      </div>
    </button>
  );
}
